import React, { useCallback, useMemo } from 'react';
import { Badge, Media } from 'reactstrap';
import classNames from 'classnames';
import { useHistory } from 'react-router';
import { User } from 'react-feather';
import { ClientReservation } from '@src/@data/types/clients';
import { getReservationDisplayData } from '@src/@data/utility/reservations';
import { useMessages } from '@hooks/useMessages';
import {
  getPath,
  ROUTE_RESERVATION_TIME,
  ROUTE_RESERVATION_SLOT,
} from '@src/@data/router/consts';
import { CalendarType } from '@src/@data/types/calendars';

export interface ReservationItemProps {
  reservation: Pick<
    ClientReservation,
    | 'type'
    | 'id'
    | 'slotId'
    | 'serviceTitle'
    | 'startDate'
    | 'timeZone'
    | 'endDate'
    | 'shared'
  > &
    Partial<Pick<ClientReservation, 'status'>>;
  companyId: string;
  className?: string;
  spotInfo?: string;
  price?: string;
  promo?: string;
}

export const ReservationItem: React.FC<ReservationItemProps> = ({
  reservation,
  companyId,
  className,
  spotInfo,
  price,
  promo,
}) => {
  const { messages } = useMessages();
  const history = useHistory();

  const displayData = useMemo(
    () => getReservationDisplayData(reservation),
    [reservation]
  );

  const displayDate = useMemo(() => {
    return displayData.isToday
      ? messages('reservation_date_today')
      : displayData.formattedDate;
  }, [displayData.formattedDate, displayData.isToday, messages]);

  const reservationData = useMemo(() => {
    return `${displayDate}, ${displayData.formattedTime}${
      price ? `, ${price}` : ''
    }`;
  }, [displayData.formattedTime, displayDate, price]);

  const goToReservationDetails = useCallback(() => {
    if(reservation.shared) {
        return
    }

    history.push(
      reservation.type === CalendarType.Time
        ? getPath(ROUTE_RESERVATION_TIME, {
            companyId,
            reservationId: reservation.id,
          })
        : getPath(ROUTE_RESERVATION_SLOT, {
            companyId,
            slotId: reservation.slotId || '',
          })
    );
  }, [
    companyId,
    history,
    reservation.id,
    reservation.slotId,
    reservation.type,
  ]);

  return (
    <Media
      body
      className={classNames(
        className,
        'align-items-center align-content-center cursor-pointer'
      )}
      onClick={goToReservationDetails}
    >
      <div className="d-flex justify-content-between align-items-start">
        <div className="p-0 m-0 mb-10">
          <h6 className="whitespace-normal">{reservationData}</h6>
          {!!promo && <h6 className="ml-15 text-danger text-left">{promo}</h6>}
        </div>
        {!!reservation.status && (
          <Badge color={displayData.statusColor} className="ml-75" pill>
            {messages(displayData.statusLabelKey)}
          </Badge>
        )}
      </div>
      <small className="p-0 m-0 text-secondary whitespace-normal">
        {reservation.serviceTitle}
      </small>
      {!!spotInfo && (
        <div className="d-flex align-items-center">
          <User
            className="mr-25"
            size={12}
            fill="#8a8a8a"
            stroke="#8a8a8a"
            strokeLinecap="butt"
          />
          <small className="text-secondary">{spotInfo}</small>
        </div>
      )}
    </Media>
  );
};
